import React from "react";
import { PropError, PropLabel, PropOption } from "../PropX";
import { LooseAutocompleteInput } from "../__Internal__";
import { useAutocompleteFocusCapture } from "../hooks/useAutocompleteFocusCapture";
import { useEvent } from "../hooks/useEvent";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { forkedRef } from "../utils/forkedRef";
import { Suggestions, useSuggestionsProgram } from "./suggestions";

type Props = {
  /**
   * The name attached to the input.
   */
  name?: string;

  /**
   * The id attached to the input.
   */
  id?: string;

  /**
   * The value of the input.
   */
  value: string;

  /**
   * A callback triggered when the input value changes.
   */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * The suggestions to display as autocomplete values when the input selection range is within an expression (`${...}`).
   * The values of this object can either be `true` or another object `Suggestions`. This prop is recursive,
   * and can be arbitrarily deep. Valid expressions include any key path leading to a `true` value _or_
   * to a `Suggestions` with the `Symbol.for("suggestion")` property. (The latter is useful when indicating
   * when the key path to a `Suggestions` is also a valid expression.) For example, consider the following input:
   *
   * ```ts
   * const suggestions = {
   *   MY_ENV_VAR: true,
   *
   *   _self_: {
   *     COMMIT_HASH: true,
   *     PUBLIC_URL: true,
   *   },
   *   "sample-nodejs": {
   *     COMMIT_HASH: true,
   *     PUBLIC_URL: true,
   *   },
   *   myDb: {
   *     DATABASE_URL: {
   *       [Symbol.for("suggestion")]: true,
   *       pool1: true,
   *       pool2: true,
   *     }
   *   }
   * };
   * ```
   *
   * In the above, the following would be considered valid expressions:
   *   - `${MY_ENV_VAR}`
   *   - `${_self_.COMMIT_HASH}`
   *   - `${_self_.PUBLIC_URL}`
   *   - `${sample-nodejs.COMMIT_HASH}`
   *   - `${sample-nodejs.PUBLIC_URL}`
   *   - `${myDb.DATABASE_URL}`
   *   - `${myDb.DATABASE_URL.pool1}`
   *   - `${myDb.DATABASE_URL.pool2}`
   *
   * While the following would be considered invalid:
   *   - `${_self_}`
   *   - `${sample-nodejs}`
   *   - Anything else that doesn't match a key path leading to a `true` value.
   */
  suggestions: Suggestions;

  /**
   * Placeholder text for the input.
   * @default ""
   */
  placeholder?: string;

  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the input is required.
   * @default false
   */
  required?: boolean;

  /**
   * An element to display on the leftSide of the input.
   * @default undefined
   */
  leftSide?: React.ReactNode;

  /**
   * An element to display on the rightSide of the input.
   * @default undefined
   */
  rightSide?: React.ReactNode;

  /**
   * An optional header to display above the list of options.
   * @default null
   */
  listHeader?: React.ReactNode;

  /**
   * An optional footer to display below the list of options.
   * @default null
   */
  listFooter?: React.ReactNode;

  /**
   * The tabIndex for the input.
   * @default 0
   */
  tabIndex?: 0 | -1;

  /**
   * Testing utility tag.
   * @default undefined
   */
  "data-testid"?: string;

  /**
   * A label for the input.
   * @default undefined
   */
  label?: PropLabel;
  /**
   * A custom error to display for the input. Overrides standard errors created by `required` or `pattern`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The number of items to render above and below the visible items in the list.
   * @private
   * @default 10
   */
  UNSAFE_overscanCount?: number;
};

export const EnvironmentVariableInput = React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
  const {
    id,
    name,
    value,
    label,
    leftSide,
    rightSide,
    placeholder,
    listHeader = null,
    listFooter = null,
    tabIndex = 0,
    required = false,
    disabled = false,
    UNSAFE_overscanCount = 10,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const suggestions = useSuggestionsProgram(props, { ref: inputRef });

  const testId = props["data-testid"] || "environment-variable-input";

  const propErrors = PropError.asArray(props.error);
  const [errors, propsForInput] = useHTMLInputElementValidationProjection(
    {
      ...props,
      onBlur: suggestions.onBlur,
      onChange: suggestions.onChange,
      error: [...propErrors, suggestions.error],
    },
    { ref: inputRef, validateOn: "blur" }
  );
  const { invalid } = propsForInput;

  const bin = React.useMemo(() => {
    return PropOption.bin(suggestions.options, [], { filterSearch: "", filterSelectedFromItems: true });
  }, [suggestions.options, value]);

  const onSelect = useEvent((key: string) => {
    const option = PropOption.fullMatchBySearch(suggestions.options, key);

    if (option) {
      suggestions.onSelect(option.value);
    }
  });

  const capture = useAutocompleteFocusCapture();

  return (
    <div onFocusCapture={capture.onFocusCapture} onBlurCapture={capture.onBlurCapture}>
      {label && PropLabel.renderForBeforeInput(label, { required, invalid, htmlFor: name })}
      <LooseAutocompleteInput
        id={id}
        name={name}
        ref={forkedRef(inputRef, forwardedRef)}
        value={value}
        items={bin.items}
        tabIndex={tabIndex}
        leftSide={leftSide}
        rightSide={rightSide}
        placeholder={placeholder}
        onSelect={onSelect}
        disabled={disabled}
        required={required}
        mono
        listHeader={listHeader}
        listFooter={listFooter}
        data-testid={testId}
        UNSAFE_showPopoverOnInvalid
        UNSAFE_forceHidePopover={!capture.focused}
        UNSAFE_popoverRepositionKey={errors.key}
        UNSAFE_overscanCount={UNSAFE_overscanCount}
        UNSAFE_type="textarea"
        UNSAFE_rows={1}
        {...propsForInput}
      />
      {PropError.renderGroupAfterInput(errors)}
    </div>
  );
});

EnvironmentVariableInput.displayName = "TextInput.EnvironmentVariable";
